import { DragEvent, Fragment, useState } from 'react';
import { ArrowDown, ArrowUp, Copy, Grid3x3, Trash2 } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { InputKind, InputSheet, InputValues, SweepInput, emptyInputs } from '@/reports/inputs';
import { duplicateInput, locate, moveInput, moveSection, moveSectionTo, nudgeInput, removeInput, removeSection, sweepsReading } from '@/reports/bundles/sheetEdit';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Field } from '../../reportsV2/fields';
import { FillContextProvider } from '../../reportsV2/fillContext';
import { InputControl, WIDE, hintFor } from '../../reportsV2/SheetForm';
import { DRAG_MOVE, DRAG_MOVE_SECTION, DRAG_NEW, DRAG_NEW_SECTION } from './builderControls';

/** What is selected; `naming` while a new input gets its first question, when its id follows the question. */
export type Selection = { kind: 'input'; id: string; naming?: boolean } | { kind: 'section'; id: string } | null;
/** Where a drop lands: an input's place in a section, or for a section, `section` is the gap between sections (0 before the first). */
export interface DropTarget { section: number; index: number }

const PREVIEW = { editable: false, preview: true, upload: async () => null, traces: {} };

const sweepNames = (sweeps: SweepInput[]) => [...new Set(sweeps.map((s) => s.label || s.id))].join(', ');

/** The sweeps outside a section that read an input in it: what removing the section unlinks. */
function sectionReaders(sheet: InputSheet, sectionId: string): SweepInput[] {
  const section = sheet.sections.find((s) => s.id === sectionId);
  if (!section) return [];
  const inside = new Set(section.inputs.map((i) => i.id));
  return section.inputs.flatMap((i) => sweepsReading(sheet, i.id)).filter((sweep) => !inside.has(sweep.id));
}

/** A delete waiting on the designer: one that leaves a sweep's conditions fixed in the bundle. */
type PendingDelete = { kind: 'input' | 'section'; id: string; label: string; sweeps: string };

/** A floating row of actions on the selected item. */
function ItemActions({ label, onUp, onDown, onCopy, onDelete }: { label: string; onUp: () => void; onDown: () => void; onCopy?: () => void; onDelete: () => void }) {
  const button = 'flex size-7 items-center justify-center text-white hover:bg-white/15';
  return (
    <div className="absolute -top-3.5 right-3 z-10 flex overflow-hidden rounded-md bg-sky-800 shadow-sm" role="toolbar" aria-label={label}>
      <button type="button" className={button} aria-label={I18n.get('rv2MoveUp')} onClick={(e) => { e.stopPropagation(); onUp(); }}><ArrowUp className="size-3.5" /></button>
      <button type="button" className={button} aria-label={I18n.get('rv2MoveDown')} onClick={(e) => { e.stopPropagation(); onDown(); }}><ArrowDown className="size-3.5" /></button>
      {onCopy && <button type="button" className={button} aria-label={I18n.get('rv2Duplicate')} onClick={(e) => { e.stopPropagation(); onCopy(); }}><Copy className="size-3.5" /></button>}
      <button type="button" className={button} aria-label={I18n.get('rv2Delete')} onClick={(e) => { e.stopPropagation(); onDelete(); }}><Trash2 className="size-3.5" /></button>
    </div>
  );
}

/**
 * The sheet as the operator will see it, every input drawn as it fills in,
 * but for designing: click an input to edit it, move it, copy it or delete it;
 * drag one from the palette, or an input already here, to where it should go.
 * Sections are clicked and dragged the same way, and drop between sections.
 */
export function InputsCanvas({ sheet, selection, onSelect, onChange, onDropNew }: {
  sheet: InputSheet;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onChange: (sheet: InputSheet) => void;
  onDropNew: (kind: InputKind | 'section', at: DropTarget) => void;
}) {
  const [drop, setDrop] = useState<DropTarget | null>(null);
  /** the gap between sections a dragged section would drop in */
  const [gap, setGap] = useState<number | null>(null);
  const [pending, setPending] = useState<PendingDelete | null>(null);
  const sample: InputValues = emptyInputs(sheet);

  const isSectionDrag = (e: DragEvent) => e.dataTransfer.types.includes(DRAG_NEW_SECTION) || e.dataTransfer.types.includes(DRAG_MOVE_SECTION);
  const sectionDragOver = (e: DragEvent, at: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDrop(null);
    if (gap !== at) setGap(at);
  };
  /** over a section: before it in its top half, after it in its bottom half */
  const overSection = (e: DragEvent<HTMLElement>, s: number) => {
    const box = e.currentTarget.getBoundingClientRect();
    sectionDragOver(e, e.clientY < box.top + box.height / 2 ? s : s + 1);
  };

  const dragOver = (e: DragEvent, section: number, index: number) => {
    if (!e.dataTransfer.types.includes(DRAG_NEW) && !e.dataTransfer.types.includes(DRAG_MOVE)) return;
    e.preventDefault();
    e.stopPropagation();
    if (gap !== null) setGap(null);
    if (drop?.section !== section || drop.index !== index) setDrop({ section, index });
  };
  /** over an input: before it in its top half, after it in its bottom half */
  const overInput = (e: DragEvent<HTMLElement>, section: number, index: number) => {
    const box = e.currentTarget.getBoundingClientRect();
    dragOver(e, section, e.clientY < box.top + box.height / 2 ? index : index + 1);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const at = drop;
    const between = gap;
    setDrop(null);
    setGap(null);
    if (isSectionDrag(e)) {
      if (between === null) return;
      const kind = e.dataTransfer.getData(DRAG_NEW_SECTION);
      const moving = e.dataTransfer.getData(DRAG_MOVE_SECTION);
      if (kind) onDropNew('section', { section: between, index: 0 });
      else if (moving) onChange(moveSectionTo(sheet, moving, between));
      return;
    }
    if (!at) return;
    const kind = e.dataTransfer.getData(DRAG_NEW) as InputKind | 'section';
    const moving = e.dataTransfer.getData(DRAG_MOVE);
    if (kind) onDropNew(kind, at);
    else if (moving) {
      const from = locate(sheet, moving);
      // the index was counted with the moving input still in place
      const index = from && from.section === at.section && from.index < at.index ? at.index - 1 : at.index;
      onChange(moveInput(sheet, moving, at.section, index));
    }
  };

  const deleteNow = (kind: 'input' | 'section', id: string) => {
    onChange(kind === 'input' ? removeInput(sheet, id) : removeSection(sheet, id));
    onSelect(null);
  };
  /** delete, but ask first when a sweep reads what goes */
  const askToDelete = (kind: 'input' | 'section', id: string, label: string) => {
    const readers = kind === 'input' ? sweepsReading(sheet, id) : sectionReaders(sheet, id);
    if (readers.length === 0) deleteNow(kind, id);
    else setPending({ kind, id, label, sweeps: sweepNames(readers) });
  };

  const Indicator = ({ section, index }: DropTarget) => (drop?.section === section && drop.index === index
    ? <div className="relative col-span-full h-0"><div className="absolute -top-px right-0 left-0 h-0.5 rounded bg-sky-700"><span className="absolute -top-[3px] -left-1 size-2 rounded-full bg-sky-700" /></div></div>
    : null);
  const Gap = ({ edge }: { edge: 'top' | 'bottom' }) => (
    <div className={cn('pointer-events-none absolute right-0 left-0 h-0.5 rounded bg-sky-700', edge === 'top' ? '-top-[calc(0.5rem+1px)]' : '-bottom-[calc(0.5rem+1px)]')}>
      <span className="absolute -top-[3px] -left-1 size-2 rounded-full bg-sky-700" />
    </div>
  );

  return (
    <FillContextProvider value={PREVIEW}>
      <div className="flex flex-col gap-[1rem]" onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setDrop(null); setGap(null); } }}
        // in the space between sections, a section keeps the gap it is in
        onDragOver={(e) => { if (isSectionDrag(e) && gap !== null) e.preventDefault(); }}
        onDrop={onDrop}>
        {sheet.sections.length === 0 && (
          <div className="rounded-xl border-[1px] border-dashed px-[1rem] py-10 text-center text-muted-foreground" onDragOver={(e) => (isSectionDrag(e) ? sectionDragOver(e, 0) : dragOver(e, 0, 0))}>{I18n.get('rv2EmptySheetDesign')}</div>
        )}
        {sheet.sections.map((section, s) => {
          const selected = selection?.kind === 'section' && selection.id === section.id;
          return (
            <section
              key={section.id}
              aria-label={section.title}
              draggable
              onDragStart={(e) => { e.dataTransfer.setData(DRAG_MOVE_SECTION, section.id); e.dataTransfer.effectAllowed = 'move'; }}
              onClick={(e) => { e.stopPropagation(); onSelect({ kind: 'section', id: section.id }); }}
              onDragOver={(e) => (isSectionDrag(e) ? overSection(e, s) : dragOver(e, s, section.inputs.length))}
              className={cn('relative rounded-xl border-[1px] bg-background px-[1.25rem] py-[1rem] shadow-xs', selected && 'ring-2 ring-sky-700')}
            >
              {gap === s && <Gap edge="top" />}
              {gap === s + 1 && s === sheet.sections.length - 1 && <Gap edge="bottom" />}
              {selected && (
                <ItemActions label={section.title} onUp={() => onChange(moveSection(sheet, section.id, -1))} onDown={() => onChange(moveSection(sheet, section.id, 1))}
                  onDelete={() => askToDelete('section', section.id, section.title)} />
              )}
              <header className="mb-[0.75rem] flex items-baseline justify-between gap-[0.75rem]">
                <h2 className="text-base font-semibold">{section.title}</h2>
                <span className="text-xs text-muted-foreground">{I18n.get('rv2SectionInputs', { count: String(section.inputs.length) })}</span>
              </header>
              {section.help && <p className="-mt-1.5 mb-[0.75rem] text-sm text-muted-foreground">{section.help}</p>}
              <div className="grid grid-cols-1 gap-x-[1.25rem] gap-y-[1rem] md:grid-cols-2">
                {section.inputs.map((input, i) => {
                  const isSelected = selection?.kind === 'input' && selection.id === input.id;
                  const wide = WIDE.has(input.kind) || (input.kind === 'text' && !!input.multiline);
                  const readers = sweepsReading(sheet, input.id);
                  return (
                    <Fragment key={input.id}>
                      <Indicator section={s} index={i} />
                      <div
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData(DRAG_MOVE, input.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragOver={(e) => overInput(e, s, i)}
                        onClick={(e) => { e.stopPropagation(); onSelect({ kind: 'input', id: input.id }); }}
                        className={cn('relative min-w-0 cursor-pointer rounded-lg px-2.5 py-2 transition-colors hover:bg-muted/50', wide && 'md:col-span-2', isSelected && 'bg-sky-50/60 ring-2 ring-sky-700 hover:bg-sky-50/60')}
                      >
                        {isSelected && (
                          <ItemActions label={input.label}
                            onUp={() => onChange(nudgeInput(sheet, input.id, -1))}
                            onDown={() => onChange(nudgeInput(sheet, input.id, 1))}
                            onCopy={() => { const copied = duplicateInput(sheet, input.id); onChange(copied.sheet); onSelect({ kind: 'input', id: copied.id, naming: true }); }}
                            onDelete={() => askToDelete('input', input.id, input.label)} />
                        )}
                        {/* drawn as it fills in, but not for filling in here */}
                        <div className="pointer-events-none" inert>
                          <Field input={input} hint={hintFor(input, sample[input.id])} wide={wide}>
                            <InputControl sheet={sheet} input={input} inputs={sample} onChange={() => undefined} />
                          </Field>
                        </div>
                        {readers.length > 0 && (
                          <p className="mt-1.5 inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-800">
                            <Grid3x3 className="size-3" aria-hidden /> {I18n.get('rv2LinkedToSweep', { sweeps: sweepNames(readers) })}
                          </p>
                        )}
                      </div>
                    </Fragment>
                  );
                })}
                <Indicator section={s} index={section.inputs.length} />
                {section.inputs.length === 0 && <p className="col-span-full rounded-lg border-[1px] border-dashed py-6 text-center text-xs text-muted-foreground">{I18n.get('rv2DropHere')}</p>}
              </div>
            </section>
          );
        })}
      </div>
      <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        {/* clicks in the dialog bubble through the portal; the canvas behind must not take them as a click on its background */}
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{I18n.get('rv2DeleteLinkedTitle', { label: pending?.label ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{I18n.get(pending?.kind === 'section' ? 'rv2DeleteLinkedSection' : 'rv2DeleteLinkedInput', { sweeps: pending?.sweeps ?? '' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{I18n.get('cancel')}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => { if (pending) deleteNow(pending.kind, pending.id); setPending(null); }}>{I18n.get('rv2Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FillContextProvider>
  );
}
